/**
 * Client-side tiptap extension set for 'tiptap'-format (scene-structured) books.
 *
 * This mirrors the server's canonical set in
 * api/src/utils/tiptap/tiptapContent.ts so that ProseMirror docs round-trip
 * identically between client and server: StarterKit (v3 bundles
 * paragraph/heading/bold/italic/underline/strike, lists, blockquote, horizontal
 * rule, hard break, link, history, ...) + Image + a Pid global attribute.
 *
 * The one client-only addition is a ProseMirror plugin that auto-assigns a
 * stable pid to every new block, so inline-comment anchors always have
 * something to point at (the server does the equivalent when it ingests HTML).
 */
import {Extension} from '@tiptap/core';
import {Plugin, PluginKey} from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';

import {PID_TYPES} from './sceneCommentMapping';
import {newStableId as newPid} from './ids';
import {Callout} from './calloutNode';
import {buildTableExtensions} from './tableExtensions';
import {lowlight} from './codeHighlight';

/**
 * Stable block identity, serialized to/from `data-pid` in HTML — identical in
 * behaviour to the server's Pid extension. Also installs the auto-assign
 * plugin: after any doc-changing transaction, every pid block that lacks a pid
 * (a freshly typed/split paragraph) or duplicates one (a copy) gets a fresh
 * crypto.randomUUID().
 */
export const Pid = Extension.create({
  name: 'pid',

  addGlobalAttributes() {
    return [
      {
        types: PID_TYPES,
        attributes: {
          pid: {
            default: null,
            // A split block should not clone its neighbour's pid.
            keepOnSplit: false,
            parseHTML: element => element.getAttribute('data-pid'),
            renderHTML: attributes =>
              attributes.pid ? {'data-pid': attributes.pid} : {}
          }
        }
      }
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('pidAutoAssign'),
        appendTransaction: (transactions, oldState, newState) => {
          if (!transactions.some(tr => tr.docChanged)) return null;
          const seen = new Set();
          const tr = newState.tr;
          let modified = false;
          newState.doc.descendants((node, pos) => {
            if (!PID_TYPES.includes(node.type.name)) return;
            const pid = node.attrs.pid;
            if (!pid || seen.has(pid)) {
              tr.setNodeAttribute(pos, 'pid', newPid());
              modified = true;
            } else {
              seen.add(pid);
            }
          });
          return modified ? tr : null;
        }
      })
    ];
  }
});

/**
 * The extension array for a scene editor/reader. Kept as a factory so each
 * editor instance gets its own extension objects (tiptap stores per-instance
 * state on them).
 */
export function buildSceneExtensions() {
  return [
    // Disable StarterKit's plain code block and use CodeBlockLowlight instead:
    // same `codeBlock` node + `language` attr (so the schema still matches the
    // server, which keeps StarterKit's code block), plus syntax-highlight
    // decorations from the curated lowlight instance. Unknown languages fall
    // back to plaintext.
    StarterKit.configure({codeBlock: false}),
    CodeBlockLowlight.configure({lowlight}),
    // Must match the server (api/src/utils/tiptap/tiptapContent.ts): with the
    // default allowBase64:false the Image parseHTML rule is
    // `img[src]:not([src^="data:"])`, so a pasted or freshly-imported base64
    // image is dropped from the document entirely rather than merely losing its
    // src. A drift guard test asserts both sides agree.
    Image.configure({allowBase64: true}),
    Pid,
    Callout,
    ...buildTableExtensions()
  ];
}

export const EMPTY_DOC = {type: 'doc', content: [{type: 'paragraph'}]};

/** Parse a stored scene.content JSON string into a doc, tolerating junk. */
export function parseSceneDoc(content) {
  if (!content) return EMPTY_DOC;
  try {
    const doc = JSON.parse(content);
    if (doc && doc.type === 'doc') return doc;
  } catch (_e) {
    // fall through
  }
  return EMPTY_DOC;
}
